import i18next from 'i18next'

export interface ErrorResponseRow {
  errorType: string
  description?: string
  params?: Record<string, unknown>
}

export interface ErrorResponse {
  // A non-JSON response is read as text (see httpHelper's asyncRequest), and at least one caller
  // models a plain string body explicitly (AdminManageCMSBannerPage), so this is not always JSON.
  body?: ErrorResponseRow[] | string
  status?: number
}

/**
 * A translation of the error type is preferred, but many types have none, and several distinct
 * failures can share one type: every window validation on a recurring route raises
 * INVALID_TIME_FORMAT. The server's description says which shipment and which window, so it is
 * the fallback rather than the untranslated key, which is what the planner used to be shown.
 * Same order of preference as the import error list in ImportOrders.
 */
const rowMessage = (row: ErrorResponseRow): string =>
  // The row itself is spread, not only its params: some translations interpolate {{description}}.
  i18next.t(`errors.${row.errorType}`, {
    ...row,
    ...row.params,
    defaultValue: row.description || row.errorType
  })

/** More than this and the message is unreadable; the count tells the planner there is more to fix. */
const MAX_ROWS_SHOWN = 5

/**
 * The readable text for a rejected command.
 *
 * Every row is rendered, not just the first: a command composed with `raise` stops at its first
 * failure and sends one, but one accumulating with `zipOrAccumulate` sends them all, and dropping
 * the rest would hide errors the planner still has to fix. Beyond MAX_ROWS_SHOWN only the count is
 * given, since a wall of text hides as much as a truncated one.
 */
export const errorResponseMessage = (err: unknown): string => {
  // setToastError's own contract is "a helper for showing an Error object", and two callers pass
  // err.message directly rather than the rejection itself - neither carries a structured body, but
  // both are already human-readable text. A structured body wins when both are present.
  const directMessage = typeof err === 'string' ? err : err instanceof Error ? err.message : undefined
  const body = (err as ErrorResponse | undefined)?.body ?? directMessage
  // A string body carries no description or params, only the raw error code as text; treat it as
  // a single row with the string standing in for its errorType, same fallback order as any other.
  // httpHelper's query() rejects with the fetch Response itself, whose body is a ReadableStream.
  const rows =
    typeof body === 'string'
      ? body
        ? [{ errorType: body }]
        : []
      : Array.isArray(body)
        ? body.filter((row) => row?.errorType)
        : []
  if (rows.length === 0) {
    return i18next.t('error.somethingWentWrong')
  }

  const shown = rows.slice(0, MAX_ROWS_SHOWN).map(rowMessage)
  const remaining = rows.length - shown.length

  return [...shown, ...(remaining > 0 ? [i18next.t('error.andMoreErrors', { count: remaining })] : [])].join(' · ')
}
